import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { contactOverviewApi, type ActionPriority, type MyNextActions } from '../api/contactOverview'
import { Callout, DataTable, FilterBar, DataTableEmptyRow, LoadingState, Page, PageHeader, Select, StatusPill, type PillTone } from '../components/ui'

/**
 * F-076 (TASK-0330) — "Prossime azioni": le azioni consigliate sui clienti dell'agente, in ordine di priorità,
 * ciascuna con la motivazione. Sono indicazioni: non scrivono nulla e non contattano il cliente. Nessun importo.
 */

const PRIORITY_TONE: Record<ActionPriority, PillTone> = {
  Alta: 'error',
  Media: 'warning',
  Bassa: 'neutral',
}

const LIMITS = [25, 50, 100, 200]

export function NextBestActionsPage() {
  const [limit, setLimit] = useState(50)
  const [data, setData] = useState<MyNextActions | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    contactOverviewApi.myNextActions(limit)
      .then(d => { if (!cancelled) setData(d) })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Errore nel caricamento delle azioni') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [limit])

  return (
    <Page>
      <PageHeader
        title="Prossime azioni"
        icon="bolt"
      />

      <FilterBar>
        <Select
          id="nba-limit"
          label="Mostra"
          value={limit}
          onChange={e => setLimit(Number(e.target.value))}
        >
          {LIMITS.map(n => <option key={n} value={n}>{n}</option>)}
        </Select>
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}
      {loading && !data && <LoadingState />}

      {data && (
        <>
          <div className="text-muted md-typescale-body-small mb-2">
            {data.total.toLocaleString('it-IT')} azioni su {data.contactsConsidered.toLocaleString('it-IT')} clienti
            {data.total > data.items.length ? ` — mostrate le prime ${data.items.length}` : ''}.
            {data.contactsTruncated && ' L’elenco considera solo i primi clienti: restringi il portafoglio per vedere il resto.'}
          </div>
              <DataTable>
                <thead>
                  <tr>
                    <th>Priorità</th>
                    <th>Cliente</th>
                    <th>Azione</th>
                    <th>Perché</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 && (
                    <DataTableEmptyRow colSpan={4} title="Nessuna azione consigliata." />
                  )}
                  {data.items.map((a, i) => (
                    <tr key={`${a.contactId}-${a.kind}-${a.refId ?? ''}-${i}`}>
                      <td><StatusPill tone={PRIORITY_TONE[a.priority]}>{a.priority}</StatusPill></td>
                      <td><Link to={`/contacts/${a.contactId}`}>{a.contactName}</Link></td>
                      <td>{a.title}</td>
                      <td className="text-muted md-typescale-body-small">{a.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
        </>
      )}
    </Page>
  )
}
